/**
 * Pool label lookups — id → human name for accounts and models.
 *
 * A session DTO carries account and model *ids*, never their names: the server
 * joins no labels onto it. Rather than dress an id up as a name, or leave the
 * operator reading a UUID, these hooks resolve the id against the same
 * `['accounts']` and `['models']` lists the accounts page and the start dialog
 * already fetch. TanStack dedupes by query key, so calling them here reuses the
 * cached response instead of issuing another request.
 *
 * The lookups fall back to the id when a pool row is missing (an account or
 * model that has since been removed), so a stale reference still renders as the
 * honest machine value rather than an empty cell.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type PageResponse } from '@/api/client'
import type { AccountUsage } from '@/components/AccountUsage'
import { readProject, type ProjectReading } from '@/routes/projectsModel'
import type { ID, Project } from '@/types/domain'

/** The fields these lookups read — a subset of api.AccountOverview. */
interface AccountLabelRow {
  id: ID
  label: string
  identityLabel?: string
}

/** The fields these lookups read — a subset of api.ModelResponse. */
interface ModelLabelRow {
  id: ID
  displayName: string
}

/** account id → its label, falling back to the identity label, then the id. */
export function useAccountLabels(): Map<ID, string> {
  const q = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<PageResponse<AccountLabelRow>>('/accounts'),
  })
  return useMemo(() => {
    const m = new Map<ID, string>()
    for (const a of q.data?.items ?? []) {
      m.set(a.id, a.label || a.identityLabel || a.id)
    }
    return m
  }, [q.data])
}

/**
 * A live session's account, with its real quota — resolved from the same cached
 * `['accounts']` list. A session's `accountProfileId` IS the account profile's
 * `id`, so this joins a running session to the subscription's usage/capacity the
 * accounts screen already fetched, without a session→usage endpoint (there is
 * none) and without fabricating anything: `usage`/`capacity` are absent, not
 * zeroed, when the provider published none.
 */
export interface SessionAccount {
  id: ID
  label: string
  identityLabel?: string
  provider?: string
  state?: string
  canAcceptWork?: boolean
  usage?: AccountUsage
  capacity?: {
    remainingFraction?: number
    confidence?: string
    source?: string
    resetAt?: string
    windowLabel?: string
  }
}

export function useAccountByProfile(): (id: ID | undefined) => SessionAccount | undefined {
  const q = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<PageResponse<SessionAccount>>('/accounts'),
  })
  const map = useMemo(() => {
    const m = new Map<ID, SessionAccount>()
    for (const a of q.data?.items ?? []) m.set(a.id, a)
    return m
  }, [q.data])
  return (id) => (id ? map.get(id) : undefined)
}

interface HostLabelRow {
  id: ID
  name: string
}

/** model id → its display name, falling back to the id. */
export function useModelLabels(): Map<ID, string> {
  const q = useQuery({
    queryKey: ['models'],
    queryFn: () => api.get<PageResponse<ModelLabelRow>>('/models'),
  })
  return useMemo(() => {
    const m = new Map<ID, string>()
    for (const model of q.data?.items ?? []) {
      m.set(model.id, model.displayName || model.id)
    }
    return m
  }, [q.data])
}

/** host id → its name, falling back to the id.
 *
 *  The inspector printed a bare UUID where every other surface prints `SRV3`,
 *  with a comment explaining that no host list was available here. There is one:
 *  the same `['hosts']` query the fleet screen reads, and it is already cached
 *  by the time a session drawer opens. "Which machine is this on" is the whole
 *  point of that row. */
export function useHostLabels(): Map<ID, string> {
  const q = useQuery({
    queryKey: ['hosts'],
    queryFn: () => api.get<PageResponse<HostLabelRow>>('/hosts'),
  })
  return useMemo(() => {
    const m = new Map<ID, string>()
    for (const host of q.data?.items ?? []) {
      m.set(host.id, host.name || host.id)
    }
    return m
  }, [q.data])
}

/**
 * project id → what the node's scan of its checkouts adds up to.
 *
 * A session says which project it belongs to and nothing about that project.
 * The scan is where the answer already is — branch, head, dirty, detected stack
 * and the agent-configuration inventory, all on `GET /projects` (see
 * `projectsModel`) — and until now the only screen reading it was the project
 * list. On the board it answers the question the cards could not: *what is this
 * agent working on*, as opposed to which machine it happens to run on.
 *
 * Same query key as the project screen, so this costs nothing when either has
 * already fetched, and `readProject` is the same derivation — a card and the
 * project's own page cannot disagree about whether a tree is dirty.
 */
export function useProjectReadings(): Map<ID, ProjectReading> {
  const q = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<PageResponse<Project>>('/projects'),
  })
  return useMemo(() => {
    const m = new Map<ID, ProjectReading>()
    for (const p of q.data?.items ?? []) m.set(p.id, readProject(p))
    return m
  }, [q.data])
}
